<template>
  <div id="root">
    <!-- Your component's HTML template goes here -->
    <h1 v-if="unauthorized">
      Unauthorized
    </h1>
    <div v-else>
      <h2>Recruitment Depth Chart</h2>
      <div class="row align-items-center" style="display: flex;">
        <el-select
          class="select-default col-md-3"
          filterable
          v-if="isOneOfTheseRoles(['regional'])"
          clearable
          v-model="locationId"
          placeholder="Select location..."
        >
          <el-option
            v-for="item in locations"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
        <span v-if="formattedLastUpdatedAt && isReady">
          Last updated: {{ formattedLastUpdatedAt }}
        </span>
      </div>
      <div
        v-loading="!isReady"
        v-if="!isReady"
        element-loading-text="Loading Recruitment Data. This could take a moment..."
      ></div>
      <div v-else>
        <el-button
          v-if="!personId"
          type="primary"
          @click.native="modals.addRecruitmentDepthChart = true"
        >
          Add <i class="el-icon-plus el-icon--right" />
        </el-button>
        <el-button
          v-if="!personId"
          type="danger"
          @click.native="modals.removeRecruitmentDepthChart = true"
        >
          Remove <i class="el-icon-minus el-icon--right" />
        </el-button>
        <el-select
          class="select-default col-md-2"
          multiple
          style="margin:10px;"
          v-model="selectedGenders"
          placeholder="Gender..."
        >
          <el-option
            v-for="gender in genders"
            :key="gender.id"
            :label="gender.name"
            :value="gender.name"
          />
        </el-select>
        <el-select
          class="select-default col-md-2"
          multiple
          style="margin:10px;"
          v-model="selectedSchoolYears"
          placeholder="School Year..."
        >
          <el-option
            v-for="schoolYear in schoolYears"
            :key="schoolYear.id"
            :label="schoolYear.name"
            :value="schoolYear.name"
          />
        </el-select>
        <el-tooltip
          content="For recruitment purposes,
          involvement data on this page represents the student's highest involvement from the last two years."
          placement="top"
        >
          <el-select
            class="select-default col-md-2"
            multiple
            style="margin:10px;"
            v-model="selectedInvolvements"
            placeholder="Involvement..."
          >
            <el-option
              v-for="involvement in involvements"
              :key="involvement.id"
              :label="involvement.name"
              :value="involvement.name"
            />
          </el-select>
        </el-tooltip>
        <el-select
          class="select-default col-md-2"
          multiple
          style="margin:10px;"
          v-model="selectedAffinities"
          placeholder="Affinity..."
        >
          <el-option
            v-for="affinity in affinities"
            :key="affinity.id"
            :label="affinity.name"
            :value="affinity.name"
          />
        </el-select>
        <!-- Lists -->
        <h3 v-if="isReady && !locationHasRDCs && !updating">
          No Recruitment Depth Chart Data For This Location
        </h3>
        <div class="horizontal-scrollable">
          <div class="row no-wrap">
            <div class="col-md">
              <p style="text-align:center" class="rdc-headings">
                Potential Candidates ({{ displayedPotentialCandidatesList.length }})
                <el-tooltip
                  content="This column is automatically populated weekly with any junior or above who has
                   been to at least 4 bible studies or is a disciple. Don't see someone who should be here? Add them!"
                  placement="top"
                >
                  <i class="el-icon-question question-icon"></i>
                </el-tooltip>
              </p>
              <div
                v-loading="updating"
                v-if="updating"
                element-loading-text="Updating potential candidates. This could take a moment..."
                class="loading-spinner"
              ></div>
              <div v-else>
                <div class="scrollable-container">
                  <draggable
                    v-if="!personId"
                    class="list-group"
                    tag="ul"
                    :list="displayedPotentialCandidatesList"
                    v-bind="dragOptions"
                    @start="onDragStart"
                    :move="onMove"
                    @end="onDragEnd"
                    id="potential-candidates"
                  >
                    <transition-group type="transition" :name="'flip-list'">
                      <li
                        class="list-group-item"
                        v-for="(element, index) in displayedPotentialCandidatesList"
                        :key="`${element.id}-${index}`"
                        @click="openRecruitmentNotes(element)"
                      >
                        <div class="name-container">
                          {{ element.name }}
                          <div class="badge-container">
                            <badge :class="getBadgeClass(element.involvement)">
                              {{ getAbbreviatedInvolvement(element.involvement) }}
                            </badge>
                            <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                            <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                            <i v-if="element.is_greek" class="fa fa-university"></i>
                          </div>
                        </div>
                      </li>
                    </transition-group>
                  </draggable>
                  <div v-if="personId">
                    <transition-group type="transition" :name="'flip-list'">
                      <li
                        class="list-group-item no-drag"
                        v-for="(element, index) in displayedPotentialCandidatesList"
                        :key="`${element.id}-${index}`"
                        @click="openRecruitmentNotes(element)"
                      >
                        <div class="name-container">
                          {{ element.name }}
                          <div class="badge-container">
                            <badge :class="getBadgeClass(element.involvement)">
                              {{ getAbbreviatedInvolvement(element.involvement) }}
                            </badge>
                            <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                            <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                            <i v-if="element.is_greek" class="fa fa-university"></i>
                          </div>
                        </div>
                      </li>
                    </transition-group>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md">
              <p style="text-align:center" class="rdc-headings">
                Top Candidates ({{ displayedTopCandidatesList.length }})
              </p>
              <div class="scrollable-container">
                <draggable
                  v-if="!personId"
                  element="span"
                  v-model="displayedTopCandidatesList"
                  v-bind="dragOptions"
                  @start="onDragStart"
                  :move="onMove"
                  @end="onDragEnd"
                  id="top-candidates"
                >
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item"
                      v-for="(element, index) in displayedTopCandidatesList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </draggable>
                <div v-if="personId">
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item no-drag"
                      v-for="(element, index) in displayedTopCandidatesList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </div>
              </div>
            </div>
            <div class="col-md">
              <p style="text-align:center" class="rdc-headings">
                Invited To Apply ({{ displayedInvitedToApplyList.length }})
              </p>
              <div class="scrollable-container">
                <draggable
                  v-if="!personId"
                  element="span"
                  v-model="displayedInvitedToApplyList"
                  v-bind="dragOptions"
                  @start="onDragStart"
                  :move="onMove"
                  @end="onDragEnd"
                  id="invited-to-apply"
                >
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item"
                      v-for="(element, index) in displayedInvitedToApplyList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </draggable>
                <div v-if="personId">
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item no-drag"
                      v-for="(element, index) in displayedInvitedToApplyList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </div>
              </div>
            </div>
            <div class="col-md">
              <p style="text-align:center" class="rdc-headings">
                Applied ({{ displayedAppliedList.length }})
              </p>
              <div class="scrollable-container">
                <draggable
                  v-if="!personId"
                  element="span"
                  v-model="displayedAppliedList"
                  v-bind="dragOptions"
                  @start="onDragStart"
                  :move="onMove"
                  @end="onDragEnd"
                  id="applied"
                >
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item"
                      v-for="(element, index) in displayedAppliedList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </draggable>
                <div v-if="personId">
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item no-drag"
                      v-for="(element, index) in displayedAppliedList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </div>
              </div>
            </div>
            <div class="col-md">
              <p style="text-align:center" class="rdc-headings">
                Declined Invitation ({{ displayedDeclinedInvitationList.length }})
              </p>
              <div class="scrollable-container">
                <draggable
                  v-if="!personId"
                  element="span"
                  v-model="displayedDeclinedInvitationList"
                  v-bind="dragOptions"
                  @start="onDragStart"
                  :move="onMove"
                  @end="onDragEnd"
                  id="declined-invitation"
                >
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item"
                      v-for="(element, index) in displayedDeclinedInvitationList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </draggable>
                <div v-if="personId">
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item no-drag"
                      v-for="(element, index) in displayedDeclinedInvitationList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </div>
              </div>
            </div>
            <div class="col-md">
              <p style="text-align:center" class="rdc-headings">
                Not Invited to Apply ({{ displayedNotInvitedToApplyList.length }})
              </p>
              <div class="scrollable-container">
                <draggable
                  v-if="!personId"
                  element="span"
                  v-model="displayedNotInvitedToApplyList"
                  v-bind="dragOptions"
                  @start="onDragStart"
                  :move="onMove"
                  @end="onDragEnd"
                  id="not-invited-to-apply"
                >
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item"
                      v-for="(element, index) in displayedNotInvitedToApplyList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </draggable>
                <div v-if="personId">
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item no-drag"
                      v-for="(element, index) in displayedNotInvitedToApplyList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </div>
              </div>
            </div>
            <div class="col-md">
              <p style="text-align:center" class="rdc-headings">
                Hired ({{ hiredList.length }})
              </p>
              <div class="scrollable-container">
                <draggable
                  v-if="!personId"
                  element="span"
                  v-model="displayedHiredList"
                  v-bind="dragOptions"
                  @start="onDragStart"
                  :move="onMove"
                  @end="onDragEnd"
                  id="hired"
                >
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item"
                      v-for="(element, index) in displayedHiredList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </draggable>
                <div v-if="personId">
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item no-drag"
                      v-for="(element, index) in displayedHiredList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </div>
              </div>
            </div>
            <div class="col-md">
              <p style="text-align:center" class="rdc-headings">
                Not Hired ({{ displayedNotHiredList.length }})
              </p>
              <div class="scrollable-container">
                <draggable
                  v-if="!personId"
                  element="span"
                  v-model="displayedNotHiredList"
                  v-bind="dragOptions"
                  @start="onDragStart"
                  :move="onMove"
                  @end="onDragEnd"
                  id="not-hired"
                >
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item"
                      v-for="(element, index) in displayedNotHiredList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </draggable>
                <div v-if="personId">
                  <transition-group name="no" class="list-group" tag="ul">
                    <li
                      class="list-group-item no-drag"
                      v-for="(element, index) in displayedNotHiredList"
                      :key="`${element.id}-${index}`"
                      @click="openRecruitmentNotes(element)"
                    >
                      <div class="name-container">
                        {{ element.name }}
                        <div class="badge-container">
                          <badge :class="getBadgeClass(element.involvement)">
                            {{ getAbbreviatedInvolvement(element.involvement) }}
                          </badge>
                          <span :class="getGenderClass(element.gender)" class="gender-dot"></span>
                          <i v-if="element.is_varsity" class="fa fa-futbol-o"></i>
                          <i v-if="element.is_greek" class="fa fa-university"></i>
                        </div>
                      </div>
                    </li>
                  </transition-group>
                </div>
              </div>
            </div>
          </div>
        </div>
        <add-recruitment-depth-chart-modal
          :show="modals.addRecruitmentDepthChart"
          :person-id="personId"
          @close="modals.addRecruitmentDepthChart = false"
          @save="getRecruitmentDepthChart"
        />
        <remove-recruitment-depth-chart-modal
          :show="modals.removeRecruitmentDepthChart"
          :person-id="personId"
          @close="modals.removeRecruitmentDepthChart = false"
          @save="getRecruitmentDepthChart"
        />
        <recruitment-notes-modal
          :show="modals.recruitmentNotes"
          :person="selectedPerson"
          :get-badge-class="getBadgeClass"
          :get-abbreviated-involvement="getAbbreviatedInvolvement"
          @close="modals.recruitmentNotes = false"
          @note-saved="lastUpdatedAt = new Date().toISOString()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapState, mapGetters } from 'vuex';
import { Badge } from 'src/components/UIComponents';
import { RepositoryFactory } from '../../repositories/repositoryFactory';
import AddRecruitmentDepthChartModal from './AddRecruitmentDepthChartModal';
import RemoveRecruitmentDepthChartModal from './RemoveRecruitmentDepthChartModal';
import RecruitmentNotesModal from './RecruitmentNotesModal';

const RecruitmentDepthChartRepository = RepositoryFactory.get('recruitmentDepthCharts');

export default {
    name: 'RecruitmentDepthChart',
    components: {
        draggable,
        AddRecruitmentDepthChartModal,
        RemoveRecruitmentDepthChartModal,
        RecruitmentNotesModal,
        Badge
    },
    props: {
        personId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            title: 'Recruitment Depth Chart',
            namespace: 'RecruitmentDepthChart',
            unauthorized: false,
            potentialCandidatesList: [],
            topCandidatesList: [],
            invitedToApplyList: [],
            appliedList: [],
            declinedInvitationList: [],
            notInvitedToApplyList: [],
            hiredList: [],
            notHiredList: [],
            // displayed is different than the actual list because of the filtering
            displayedPotentialCandidatesList: [],
            displayedTopCandidatesList: [],
            displayedInvitedToApplyList: [],
            displayedAppliedList: [],
            displayedDeclinedInvitationList: [],
            displayedNotInvitedToApplyList: [],
            displayedHiredList: [],
            displayedNotHiredList: [],
            editable: true,
            isReady: false,
            transferring: false,
            isDragging: false,
            delayedDragging: false,
            selectedGenders: [],
            selectedSchoolYears: [],
            selectedInvolvements: [],
            selectedAffinities: [],
            draggedItem: null,
            targetListId: null,
            locationHasRDCs: true,
            updating: false,
            selectedPerson: null,
            lastUpdatedAt: null,
            genders: [
                { id: 'Male', name: 'Male' },
                { id: 'Female', name: 'Female' }
            ],
            schoolYears: [
                { id: 'freshman', name: 'Freshman' },
                { id: 'sophomore', name: 'Sophomore' },
                { id: 'junior', name: 'Junior' },
                { id: 'senior', name: 'Senior' },
                { id: 'senior_plus', name: 'Senior+' },
                { id: 'grad_student', name: 'Grad Student' }
            ],
            involvements: [
                { id: 'na', name: 'N/A' },
                { id: 'bspx1', name: 'BSPx1' },
                { id: 'bspx4', name: 'BSPx4' },
                { id: 'growing_disciple', name: 'Growing Disciple' },
                { id: 'commissioned_disciple', name: 'Commissioned Disciple' },
                { id: 'slbs', name: 'SLBS' },
                { id: 'growing_disciple_maker', name: 'Growing Disciple Maker' },
                { id: 'disciple_maker', name: 'Disciple Maker' }
            ],
            affinities: [
                { id: 'greek', name: 'Greek' },
                { id: 'varsity', name: 'Varsity' }
            ],
            modals: {
                addRecruitmentDepthChart: false,
                removeRecruitmentDepthChart: false,
                recruitmentNotes: false
            },
        };
    },
    watch: {
        personId() {
            this.getRecruitmentDepthChart();
        },
        isDragging(newValue) {
            if (newValue) {
                this.delayedDragging = true;
                return;
            }
            this.$nextTick(() => {
                this.delayedDragging = false;
            });
        },
        selectedGenders() {
            this.updateDisplayLists();
        },
        selectedSchoolYears() {
            this.updateDisplayLists();
        },
        selectedInvolvements() {
            this.updateDisplayLists();
        },
        selectedAffinities() {
            this.updateDisplayLists();
        },
        potentialCandidatesList() {
            this.updateDisplayLists();
        },
        topCandidatesList() {
            this.updateDisplayLists();
        },
        invitedToApplyList() {
            this.updateDisplayLists();
        },
        appliedList() {
            this.updateDisplayLists();
        },
        declinedInvitationList() {
            this.updateDisplayLists();
        },
        notInvitedToApplyList() {
            this.updateDisplayLists();
        },
        hiredList() {
            this.updateDisplayLists();
        },
        notHiredList() {
            this.updateDisplayLists();
        },
    },
    computed: {
        ...mapState({
            recruitmentDepthChart: (state) => state.RecruitmentDepthChart.recruitmentDepthChart,
            error: (state) => state.RecruitmentDepthChart.error,
            locations: (state) => state.userLocations,
        }),
        ...mapGetters({
            locationFromStore: 'lastLocation'
        }),
        locationId: {
            get() {
                return this.locationFromStore;
            },
            set(newLocation) {
                this.$store.commit('setLastLocation', newLocation);
                this.getRecruitmentDepthChart();
            }
        },
        dragOptions() {
            return {
                animation: 150,
                group: 'description',
                disabled: !this.editable,
                ghostClass: 'ghost',
                scrollSensitivity: 100, // Sensitivity at which scrolling starts (default is 30)
                scrollSpeed: 30, // Speed at which the window should scroll
            };
        },
        listString() {
            return JSON.stringify(this.list, null, 2);
        },
        list2String() {
            return JSON.stringify(this.list2, null, 2);
        },
        formattedLastUpdatedAt() {
            if (!this.lastUpdatedAt) return '';
            let date;
            if (this.lastUpdatedAt.includes('T')) {
                // ISO format
                date = new Date(this.lastUpdatedAt);
            } else {
                // Legacy format: 'YYYY-MM-DD HH:mm:ss'
                const isoUtc = `${this.lastUpdatedAt.replace(' ', 'T')}Z`;
                date = new Date(isoUtc);
            }
            // Format without seconds
            const options = {
                year: 'numeric', month: 'short', day: 'numeric'
            };
            return date.toLocaleString(undefined, options);
        },
    },
    methods: {
        // i dont know what this is, but russell had it
        getIdFromIndex(index) {
            return this.recruitmentDepthCharts[index].id;
        },
        getRecruitmentDepthChart() {
            this.isReady = false;
            // assume the location has recruitment depth chart data
            this.locationHasRDCs = true;
            this.$store.dispatch(
                `${this.namespace}/getRecruitmentDepthChartByLocation`, { locationId: this.locationId }
            )
                .then((response) => {
                    if (response) {
                        this.lastUpdatedAt = response.data.last_updated_at;
                        this.potentialCandidatesList = response.data.data['Potential Candidate'].map((chart) => (
                            {
                                name: chart.chart_member,
                                id: chart.chart_member_id,
                                gender: chart.gender,
                                school_year: chart.school_year,
                                involvement: chart.involvement,
                                recruitment_depth_chart_column_id: chart.recruitment_depth_chart_column_id,
                                is_greek: chart.is_greek,
                                is_varsity: chart.is_varsity,
                            }
                        )) || [];
                        this.topCandidatesList = response.data.data['Top Candidate'].map((chart) => (
                            {
                                name: chart.chart_member,
                                id: chart.chart_member_id,
                                gender: chart.gender,
                                school_year: chart.school_year,
                                involvement: chart.involvement,
                                recruitment_depth_chart_column_id: chart.recruitment_depth_chart_column_id,
                                is_greek: chart.is_greek,
                                is_varsity: chart.is_varsity,
                            }
                        )) || [];
                        this.invitedToApplyList = response.data.data['Invited to Apply'].map((chart) => (
                            {
                                name: chart.chart_member,
                                id: chart.chart_member_id,
                                gender: chart.gender,
                                school_year: chart.school_year,
                                involvement: chart.involvement,
                                recruitment_depth_chart_column_id: chart.recruitment_depth_chart_column_id,
                                is_greek: chart.is_greek,
                                is_varsity: chart.is_varsity,
                            }
                        )) || [];
                        // eslint-disable-next-line
                        this.appliedList = response.data.data['Applied'].map((chart) => (
                            {
                                name: chart.chart_member,
                                id: chart.chart_member_id,
                                gender: chart.gender,
                                school_year: chart.school_year,
                                involvement: chart.involvement,
                                recruitment_depth_chart_column_id: chart.recruitment_depth_chart_column_id,
                                is_greek: chart.is_greek,
                                is_varsity: chart.is_varsity,
                            }
                        )) || [];
                        this.declinedInvitationList = response.data.data['Declined Invitation'].map((chart) => (
                            {
                                name: chart.chart_member,
                                id: chart.chart_member_id,
                                gender: chart.gender,
                                school_year: chart.school_year,
                                involvement: chart.involvement,
                                recruitment_depth_chart_column_id: chart.recruitment_depth_chart_column_id,
                                is_greek: chart.is_greek,
                                is_varsity: chart.is_varsity,
                            }
                        )) || [];
                        this.notInvitedToApplyList = response.data.data['Not Invited to Apply'].map((chart) => (
                            {
                                name: chart.chart_member,
                                id: chart.chart_member_id,
                                gender: chart.gender,
                                school_year: chart.school_year,
                                involvement: chart.involvement,
                                recruitment_depth_chart_column_id: chart.recruitment_depth_chart_column_id,
                                is_greek: chart.is_greek,
                                is_varsity: chart.is_varsity,
                            }
                        )) || [];
                        this.hiredList = response.data.data.Hired.map((chart) => (
                            {
                                name: chart.chart_member,
                                id: chart.chart_member_id,
                                gender: chart.gender,
                                school_year: chart.school_year,
                                involvement: chart.involvement,
                                recruitment_depth_chart_column_id: chart.recruitment_depth_chart_column_id,
                                is_greek: chart.is_greek,
                                is_varsity: chart.is_varsity,
                            }
                        )) || [];
                        this.notHiredList = response.data.data['Not Hired'].map((chart) => (
                            {
                                name: chart.chart_member,
                                id: chart.chart_member_id,
                                gender: chart.gender,
                                school_year: chart.school_year,
                                involvement: chart.involvement,
                                recruitment_depth_chart_column_id: chart.recruitment_depth_chart_column_id,
                                is_greek: chart.is_greek,
                                is_varsity: chart.is_varsity,
                            }
                        )) || [];
                        // check if the location has any data, if not set locationHasRDCs to false
                        if (this.potentialCandidatesList.length === 0
                      && this.topCandidatesList.length === 0
                      && this.invitedToApplyList.length === 0
                      && this.appliedList.length === 0
                      && this.declinedInvitationList.length === 0
                      && this.notInvitedToApplyList.length === 0
                      && this.hiredList.length === 0
                      && this.notHiredList.length === 0) {
                            this.locationHasRDCs = false;
                        }
                        this.isReady = true;
                        this.updateDisplayLists();
                    }
                }).catch((error) => {
                    this.isReady = true;
                    if (error.response.status === 403) {
                        this.unauthorized = true;
                    } else {
                        this.$message.error('Error getting recruitment depth chart data');
                    }
                });
        },
        filterList(list) {
            return list.filter((item) => {
                const genderMatch = this.selectedGenders.length === 0 || this.selectedGenders.includes(item.gender);
                // eslint-disable-next-line
                const schoolYearMatch = this.selectedSchoolYears.length === 0 || this.selectedSchoolYears.includes(item.school_year);
                // eslint-disable-next-line
                const involvementMatch = this.selectedInvolvements.length === 0 || this.selectedInvolvements.includes(item.involvement);
                // Affinity logic
                let affinityMatch = true;
                if (this.selectedAffinities.length > 0) {
                    if (this.selectedAffinities.includes('Greek')) {
                        affinityMatch = affinityMatch && !!item.is_greek;
                    }
                    if (this.selectedAffinities.includes('Varsity')) {
                        affinityMatch = affinityMatch && !!item.is_varsity;
                    }
                }

                return genderMatch && schoolYearMatch && involvementMatch && affinityMatch;
            });
        },
        updateDisplayLists() {
            this.displayedPotentialCandidatesList = this.filterList(this.potentialCandidatesList);
            this.displayedTopCandidatesList = this.filterList(this.topCandidatesList);
            this.displayedInvitedToApplyList = this.filterList(this.invitedToApplyList);
            this.displayedAppliedList = this.filterList(this.appliedList);
            this.displayedDeclinedInvitationList = this.filterList(this.declinedInvitationList);
            this.displayedNotInvitedToApplyList = this.filterList(this.notInvitedToApplyList);
            this.displayedHiredList = this.filterList(this.hiredList);
            this.displayedNotHiredList = this.filterList(this.notHiredList);
        },
        onDragStart(event) {
            const draggedElement = event.item._underlying_vm_;
            this.draggedItem = draggedElement.id;
            this.isDragging = true;
        },
        onMove(event) {
            this.targetListId = event.relatedContext.component.$attrs.id;
        },
        onDragEnd(event) {
            this.isDragging = false;
            this.transferring = true;

            const draggedElement = event.item._underlying_vm_;
            const newListId = this.targetListId;

            if (!newListId) {
                console.error('New list ID not found');
                this.transferring = false;
                return false;
            }

            const oldListId = draggedElement.recruitment_depth_chart_column_id;
            draggedElement.recruitment_depth_chart_column_id = newListId;

            this.updateMasterLists(newListId, oldListId, draggedElement);
            this.draggedItem = null;

            RecruitmentDepthChartRepository.transferRecruitmentDepthChart(
                draggedElement.id,
                newListId,
                this.locationId
            )
                .then((response) => {
                    this.saving = false;
                    this.$emit('recruitmentDepthChartChange');
                    this.$message(response.data.message);
                    this.transferring = false;
                    this.targetListId = null;
                    // Update last updated time on frontend
                    this.lastUpdatedAt = new Date().toISOString();
                })
                .catch((error) => {
                    this.saving = false;
                    this.$message.error(error.response.data.message);

                    // revert to the old list if there's an error
                    draggedElement.recruitment_depth_chart_column_id = oldListId;
                    this.updateMasterLists(oldListId, newListId, draggedElement);

                    this.transferring = false;
                    this.targetListId = null;
                });
            return true;
        },
        updateMasterLists(newListId, oldListId, draggedElement) {
            // this method ensures that changes in the filtered displayed lists occur in the master lists
            // first, map ids to strings if needed
            switch (oldListId) {
            case 72:
                oldListId = 'potential-candidates';
                break;
            case 73:
                oldListId = 'top-candidates';
                break;
            case 74:
                oldListId = 'invited-to-apply';
                break;
            case 75:
                oldListId = 'applied';
                break;
            case 76:
                oldListId = 'declined-invitation';
                break;
            case 77:
                oldListId = 'not-invited-to-apply';
                break;
            case 95:
                oldListId = 'hired';
                break;
            case 96:
                oldListId = 'not-hired';
                break;
            default:
                break;
            }

            switch (newListId) {
            case 72:
                newListId = 'potential-candidates';
                break;
            case 73:
                newListId = 'top-candidates';
                break;
            case 74:
                newListId = 'invited-to-apply';
                break;
            case 75:
                newListId = 'applied';
                break;
            case 76:
                newListId = 'declined-invitation';
                break;
            case 77:
                newListId = 'not-invited-to-apply';
                break;
            case 95:
                newListId = 'hired';
                break;
            case 96:
                newListId = 'not-hired';
                break;
            default:
                break;
            }

            // don't do any of this if it's the same list
            if (oldListId === newListId) {
                return;
            }

            // remove the dragged element from old master list
            this.removeFromList(oldListId, draggedElement);

            // add the dragged element to new master list
            this.addToList(newListId, draggedElement);

            this.updateDisplayLists();
        },
        removeFromList(listId, draggedElement) {
            // after element is dragged to new displayed list, remove the element from the corresponding master list
            let index;
            switch (listId) {
            case 'potential-candidates':
                index = this.potentialCandidatesList.findIndex((item) => item.id === draggedElement.id);
                if (index !== -1) {
                    this.potentialCandidatesList.splice(index, 1);
                }
                break;
            case 'top-candidates':
                index = this.topCandidatesList.findIndex((item) => item.id === draggedElement.id);
                if (index !== -1) {
                    this.topCandidatesList.splice(index, 1);
                }
                break;
            case 'invited-to-apply':
                index = this.invitedToApplyList.findIndex((item) => item.id === draggedElement.id);
                if (index !== -1) {
                    this.invitedToApplyList.splice(index, 1);
                }
                break;
            case 'applied':
                index = this.appliedList.findIndex((item) => item.id === draggedElement.id);
                if (index !== -1) {
                    this.appliedList.splice(index, 1);
                }
                break;
            case 'declined-invitation':
                index = this.declinedInvitationList.findIndex((item) => item.id === draggedElement.id);
                if (index !== -1) {
                    this.declinedInvitationList.splice(index, 1);
                }
                break;
            case 'not-invited-to-apply':
                index = this.notInvitedToApplyList.findIndex((item) => item.id === draggedElement.id);
                if (index !== -1) {
                    this.notInvitedToApplyList.splice(index, 1);
                }
                break;
            case 'hired':
                index = this.hiredList.findIndex((item) => item.id === draggedElement.id);
                if (index !== -1) {
                    this.hiredList.splice(index, 1);
                }
                break;
            case 'not-hired':
                index = this.notHiredList.findIndex((item) => item.id === draggedElement.id);
                if (index !== -1) {
                    this.notHiredList.splice(index, 1);
                }
                break;
            default:
                break;
            }
        },
        addToList(listId, draggedElement) {
            // after element is added to the new displayed list, add to the corresponding master list
            switch (listId) {
            // make sure to avoid duplicates though
            case 'potential-candidates':
                if (!this.potentialCandidatesList.some((item) => item.id === draggedElement.id)) {
                    this.potentialCandidatesList.push(draggedElement);
                }
                break;
            case 'top-candidates':
                if (!this.topCandidatesList.some((item) => item.id === draggedElement.id)) {
                    this.topCandidatesList.push(draggedElement);
                }
                break;
            case 'invited-to-apply':
                if (!this.invitedToApplyList.some((item) => item.id === draggedElement.id)) {
                    this.invitedToApplyList.push(draggedElement);
                }
                break;
            case 'applied':
                if (!this.appliedList.some((item) => item.id === draggedElement.id)) {
                    this.appliedList.push(draggedElement);
                }
                break;
            case 'declined-invitation':
                if (!this.declinedInvitationList.some((item) => item.id === draggedElement.id)) {
                    this.declinedInvitationList.push(draggedElement);
                }
                break;
            case 'not-invited-to-apply':
                if (!this.notInvitedToApplyList.some((item) => item.id === draggedElement.id)) {
                    this.notInvitedToApplyList.push(draggedElement);
                }
                break;
            case 'hired':
                if (!this.hiredList.some((item) => item.id === draggedElement.id)) {
                    this.hiredList.push(draggedElement);
                }
                break;
            case 'not-hired':
                if (!this.notHiredList.some((item) => item.id === draggedElement.id)) {
                    this.notHiredList.push(draggedElement);
                }
                break;
            default:
                break;
            }
        },
        getBadgeClass(involvement) {
            switch (involvement) {
            case 'N/A':
                return 'badge-na';
            case 'BSPx1':
                return 'badge-bspx1';
            case 'BSPx4':
                return 'badge-bspx4';
            case 'Growing Disciple':
                return 'badge-growing-disciple';
            case 'Commissioned Disciple':
                return 'badge-commissioned-disciple';
            case 'SLBS':
                return 'badge-slbs';
            case 'Growing Disciple Maker':
                return 'badge-growing-disciple-maker';
            case 'Disciple Maker':
                return 'badge-disciple-maker';
            default:
                return 'badge-na';
            }
        },
        getGenderClass(gender) {
            if (gender === 'Male') {
                return 'gender-dot-male';
            } if (gender === 'Female') {
                return 'gender-dot-female';
            }
            return '';
        },
        getAbbreviatedInvolvement(involvement) {
            switch (involvement) {
            case 'Growing Disciple Maker':
                return 'GDM';
            case 'Disciple Maker':
                return 'DM';
            case 'Growing Disciple':
                return 'GD';
            case 'Commissioned Disciple':
                return 'CD';
            default:
                return involvement;
            }
        },
        openRecruitmentNotes(person) {
            this.selectedPerson = person;
            console.log('Selected person for notes:', person);
            this.modals.recruitmentNotes = true;
        },
    },
    created() {
        this.getRecruitmentDepthChart();
    }
};
</script>

<style>
.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
.no-wrap {
  flex-wrap: nowrap;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.list-group {
  min-height: 20px;
}
.list-group-item {
  transition: background-color 0.3s ease; /* Smooth transition for background color change */
}
.list-group-item i {
  cursor: pointer;
  padding-left: 4px;
}
.list-group-item:hover {
    background-color: #f5f5f5; /* Change background on hover to indicate interactivity */
}
.name-container:hover {
    color: #264f9c; /* Change text color to blue on hover */
    cursor: pointer; /* Change cursor to pointer on hover */
}
.rdc-headings {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: .75rem 1.25rem;
    margin-bottom: -1px;
    background-color: #0c3c61;
    color: white;
    border: 1px solid rgba(0,0,0,.125);
}
#loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 15%;
  padding: 20px;
}
</style>
<style scoped>
.no-drag {
  cursor: auto !important;
}
.badge-container {
  display: flex;
  align-items: center;
  margin-left: 10px;
}
.badge-na {
  background-color: #4d4f54; /* Starting color */
  color: white;
}

.badge-bspx1 {
  background-color: #6bb0e1; /* BSPx1 - Light blue */
  color: white;
}

.badge-bspx4 {
  background-color: #5aa4d9; /* Intermediate color */
  color: white;
}

.badge-growing-disciple {
  background-color: #4b97d1; /* Intermediate color */
  color: white;
}

.badge-commissioned-disciple {
  background-color: #3d89c9; /* Intermediate color */
  color: white;
}

.badge-slbs {
  background-color: #307bc1; /* Intermediate color */
  color: white;
}

.badge-growing-disciple-maker {
  background-color: #236eb9; /* Intermediate color */
  color: white;
}

.badge-disciple-maker {
  background-color: #0c3c61; /* Dark blue */
  color: white;
}
/* Flexbox styles for alignment */
.name-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none; /* Prevent text selection */
}

.name-container .badge {
  margin-left: auto; /* Ensure the badge is pushed to the right */
}
/* Custom styles for the question icon */
.question-icon {
  cursor: pointer;
  margin-left: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-spinner{
  margin-top: 2em;
}
/* Scrollable container */
.scrollable-container {
  height: 65vh; /* Adjust as needed */
  overflow-y: auto;
  margin-bottom: 5vh;
}

.gender-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-left: 5px;
}

.gender-dot-male {
  background-color: #6bb0e1; /* Blue for males */
}

.gender-dot-female {
  background-color: #ff69b4; /* Pink for females */
}

.horizontal-scrollable {
  overflow-x: auto;
}
</style>
